import { TranslateUnauthenticatedPipe } from '../../../filters/translate-unauthenticated.pipe';
import { ItemErrorComponent } from '../../../components/item-form-error/item-form-error.component';
import { ItemFormErrorWithTranslatorComponent } from '../../../components/item-form-error-with-translator/item-form-error-with-translator.component';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SiffiLanguagePickerComponent } from '../../../components/siffi-language-picker/siffi-language-picker.component';
import { Subscription } from 'rxjs';
import Session from '../../../utils/session.service';
import Config from '../../../utils/config.service';
import UserRestServices from '../../../services/user-rest.service';
import AngularFilterHelperService from '../../../services/angular-filter-helper.service';
import AngularRoutingHelperService from '../../../services/angular-routing-helper.service';
import AppLanguageService from '../../../services/app-language.service';
import ScreeningEscalationRestService from '../../../services/screening-escalation-rest.service';

@Component({
	standalone: true,
	selector: 'reset-password',
	templateUrl: './reset-password.component.html',
	styleUrl: '../auth-public.scss',
	imports: [
		CommonModule, TranslateUnauthenticatedPipe, ItemErrorComponent,
		ItemFormErrorWithTranslatorComponent, FormsModule, RouterLink,
		SiffiLanguagePickerComponent
	]
})
export default class ResetPasswordView implements OnInit, OnDestroy {
	private session: Session = inject(Session);
	private config: Config = inject(Config);
	private userRestServices: UserRestServices = inject(UserRestServices);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private appLanguageService: AppLanguageService = inject(AppLanguageService);
	private screeningRest: ScreeningEscalationRestService = inject(ScreeningEscalationRestService);
	
	vm: any = undefined;
	private titleSubscription: Subscription = undefined;
	
	constructor() {
		this.vm = this;
	}
	
	ngOnInit() {
		var queryParams: any = this.angularRoutingHelperService.getQueryParams();
		this.determineLocation(queryParams);
		
		this.vm.isLanguageMenuOpen = false;
		this.vm.locale = this.appLanguageService.normalizeLocale(queryParams['locale'] || window.sessionStorage.getItem('locale') || 'en_GB');
		this.vm.logoSuffix = this.resolveLogoSuffix(this.vm.locale);
		this.vm.localeParams = this.getRouteParams();
		window.sessionStorage.setItem("locale", this.vm.locale);
		this.vm.locales = this.appLanguageService.getLanguagesByOrigin(this.vm.origin);
		this.vm.languages = this.config.getSettings().Languages;
		this.vm.resetPasswordCode = queryParams['resetPasswordCode'];
		this.vm.registrationToken = queryParams['rt'] || queryParams['screeningToken'];
		this.vm.action = queryParams['action'];
		if (this.vm.registrationToken) {
			this.clearScreeningAutoLogin();
		}
		var currentSession = this.session.getSession();
		this.vm.isAuthenticated = !!currentSession;
		if (this.vm.isAuthenticated && !this.vm.registrationToken) {
			this.angularRoutingHelperService.home(currentSession.user);
			return;
		}
		
		this.vm.navigateToRoot = this.navigateToRoot;
		this.vm.setLocale = this.setLocale;
		this.vm.toggleLangMenu = this.toggleLangMenu;
		this.vm.getLocaleOption = this.getLocaleOption;
		this.vm.navigateToPlatform = this.navigateToPlatform;
		this.vm.sendNewPassword = this.sendNewPassword;
		this.vm.resetPassword = this.resetPassword;

		if (this.vm.registrationToken) {
			this.userRestServices.getScreeningPendingRegistration({
				verificationToken: this.vm.registrationToken
			}).then((response) => {
				this.vm.pendingRegistration = response.body || {};
			}).catch((err) => {
				if (err && err.status === 410) {
					this.vm.showExpiredTimestampError = true;
				} else {
					this.vm.showError = true;
				}
			});
		}
		
		this.titleSubscription = this.angularFilterHelperService.watchDocumentTitleUnauthenticated('Reset_Password.Reset_Password_Title');
	};

	ngOnDestroy() {
		if (this.titleSubscription) {
			this.titleSubscription.unsubscribe();
		}
	}
	
	navigateToRoot($event) {
		$event.preventDefault();
		window.location.href = this.config.getPublicWebHomeUrl(this.vm.origin, this.vm.locale);
	}
	
	setLocale(locale) {
		this.angularRoutingHelperService.setQueryParam('locale', locale);
		setTimeout(() => {
			window.location.reload();
		}, 300);
	}

	navigateToPlatform() {
		this.userRestServices.logout(undefined, () => {
			this.session.removeSessionCookie();
			this.angularRoutingHelperService.setPath('/login', false, true, this.vm.localeParams);
		}, () => {
			this.session.removeSessionCookie();
			this.angularRoutingHelperService.setPath('/login', false, true, this.vm.localeParams);
		});
	}
	
	resetPassword(mainForm: any) {
		this.vm.mainFormSubmitted = true;
		if (mainForm.invalid) return;
		this.vm.loading = true;
		var credentials: any = {
			username: this.vm.email,
			origin: this.vm.origin,
			language: this.vm.locale
		};
		this.userRestServices.sendPasswordRecoveryMailWithLink(credentials).then(
			(response) => {
				this.vm.loading = true;
				if (response.status === 200) {
					this.vm.loading = false;
					this.vm.isSuccess = true;
				} else {
					this.vm.loading = false;
					this.vm.showError = true;
					console.log(
						"Looks like there was a problem. Status Code: " + response.status);
				}
			},
			(err) => {
				this.vm.loading = false;
				this.vm.showError = true;
				console.log(err);
			}
		);
	}
	
	sendNewPassword(passwordForm: any) {
		this.vm.passwordFormSubmitted = true;
		if (passwordForm.invalid) return;
		if (this.vm.newPasswordConfirm != this.vm.newPassword) {
			this.vm.passwordMismatch = true;
			return;
		} else {
			this.vm.passwordMismatch = false;
		}
		this.vm.loading = true;
		if (this.vm.registrationToken) {
			this.userRestServices.createScreeningAccount({
				verificationToken: this.vm.registrationToken,
				newUserPassword: this.vm.newPassword,
				deviceLanguage: this.vm.locale
			}).then((res) => {
				this.vm.loading = false;
				if (res.status === 200) {
					this.vm.isSuccess = true;
					this.vm.showError = false;
					this.screeningRest.clearPilotContext();
					this.clearScreeningAutoLogin();
				} else {
					this.vm.showError = true;
				}
			}, (err) => {
				if (err && err.status === 410) {
					this.vm.loading = false;
					this.vm.showExpiredTimestampError = true;
				} else {
					this.vm.loading = false;
					this.vm.showError = true;
				}
			});
			return;
		}

		var credentials: any = {
			resetPasswordCode: this.vm.resetPasswordCode,
			newUserPassword: this.vm.newPassword,
			origin: this.vm.origin
		};
		this.userRestServices.recoverPasswordByLink(credentials).then(
			(res) => {
				this.vm.loading = true;
				console.log(res);
				if (res.status === 200) {
					this.vm.loading = false;
					this.vm.isSuccess = true;
				} else {
					this.vm.loading = false;
					this.vm.showError = true;
					console.log("Looks like there was a problem. Status Code: " + res.status);
				}
			},
			(err) => {
				console.log(err);
				if (err.status === 403 
					&& (err.error === "RESET_PASSWORD_TIMESTAMP_EXPIRED" || err.error?.data === "RESET_PASSWORD_TIMESTAMP_EXPIRED")) {
					this.vm.loading = false;
					this.vm.showExpiredTimestampError = true;
				} else {
					this.vm.loading = false;
					this.vm.showError = true;
				}
			}
		);
	}

	toggleLangMenu() {
		this.vm.isLanguageMenuOpen = !this.vm.isLanguageMenuOpen;
	}

	getLocaleOption(locale) {
		return this.appLanguageService.getLanguage(locale);
	}

	private clearScreeningAutoLogin() {
		this.session.removeSessionCookie();
		this.userRestServices.logout(undefined, () => {
			this.session.removeSessionCookie();
		}, () => {
			this.session.removeSessionCookie();
		});
	}

	private getRouteParams() {
		var params: any = this.vm.locale ? { locale: this.vm.locale } : {};
		if (this.vm.origin && this.vm.origin !== 'siffi.com') {
			params.origin = this.vm.origin;
		}
		return params;
	}

	private determineLocation(queryParams) {
		var host: any = window.location.hostname;
		var url: any = window.location.href;
		var requestedOrigin: any = queryParams['origin'];
		if (host.includes('minudoc')) {
			this.vm.origin = host.includes('minudoc.fi') ? 'minudoc.fi' : 'minudoc.ee';
			this.vm.product = url.includes('eelvisiit') ? 'PREVISIT' : 'MINUDOC';
			this.vm.headerClass = '';
			this.vm.brandName = this.vm.product === 'PREVISIT' ? 'eelvisiit' : 'MinuDoc';
			return;
		}
		if (requestedOrigin && requestedOrigin.includes('minudoc')) {
			this.vm.origin = requestedOrigin;
			this.vm.product = 'MINUDOC';
			this.vm.headerClass = '';
			this.vm.brandName = 'MinuDoc';
			return;
		}
		this.vm.origin = host.includes('siffi') ? 'siffi.com' : (requestedOrigin || 'siffi.com');
		this.vm.product = 'SIFFI';
		this.vm.headerClass = 'o-container c-container';
		this.vm.brandName = 'Siffi';
	}

	private resolveLogoSuffix(locale: string) {
		var normalizedLocale: any = this.appLanguageService.normalizeLocale(locale);
		var languageCode: any = (normalizedLocale || 'en_GB').split('_')[0];
		if (['en', 'et', 'ru', 'fi'].indexOf(languageCode) === -1) {
			return 'en';
		}
		return languageCode;
	}
	
}
